import { useState } from "react";
import type { SimState, Vehicle } from "../domain/model";
import { isOpen } from "../domain/model";
import { coverage } from "../domain/engine";
import type { Perform } from "./ResourceManager";

export default function UnitOrders({
  state,
  vehicle: v,
  act,
  enabled,
}: {
  state: SimState;
  vehicle: Vehicle;
  act: Perform;
  enabled: boolean;
}) {
  const [targetId, setTargetId] = useState("");
  const incident = state.incidents.find((i) => i.id === v.incidentId);
  const targets = state.incidents.filter(
    (i) => isOpen(i) && i.phase !== "stabilisiert" && i.id !== v.incidentId,
  );
  const target = targets.find((i) => i.id === targetId);
  const deficits =
    incident && incident.phase !== "stabilisiert"
      ? coverage(
          {
            ...state,
            vehicles: state.vehicles.filter((unit) => unit.id !== v.id),
          },
          incident,
        ).filter((r) => r.count < r.needed)
      : [];
  const request = state.speechRequests.find(
    (r) => r.incidentId === v.incidentId,
  );
  if (v.status === 7 || v.status === 8)
    return <span className="muted small">Patient gebunden · kein Abzug</span>;
  if (!incident && !(v.status === 1 && !v.journey)) return null;
  return (
    <details className="unit-orders">
      <summary aria-label={v.name + ": Einsatzauftrag bearbeiten"}>
        Auftrag / Abziehen
      </summary>
      <div className="unit-orders-body">
        <b>{v.name}</b>
        {v.mutualAid && <small>Überlandhilfe · an diesen Einsatz gebunden · Rückgabe nach Heimfahrt</small>}
        {incident && (
          <small>
            E {incident.number} · {incident.address}
          </small>
        )}
        {request && (
          <span className="speech-unit-label">
            Sprechwunsch offen · Funkleiste unten
          </span>
        )}
        {!!deficits.length && (
          <p className="unit-deficit">
            Nach Abzug fehlen:{" "}
            {deficits.map((r) => r.needed - r.count + " " + r.kind).join(", ")}.
          </p>
        )}
        {incident && (
          <>
            {v.status === 4 && (
              <button
                disabled={!enabled || !!request}
                onClick={() =>
                  void act(
                    { type: "requestSituation", vehicleId: v.id },
                    "Lageabfrage liegt als Sprechwunsch in der Funkleiste unten.",
                  )
                }
              >
                Lagemeldung anfordern
              </button>
            )}
            {!v.mutualAid && (            <button
              disabled={!enabled}
              onClick={() =>
                void act(
                  {
                    type: "withdraw",
                    vehicleId: v.id,
                    incidentId: incident.id,
                    mode: "standby",
                  },
                  "Auftrag aufgehoben. Fahrzeug wartet einsatzbereit am Standort.",
                )
              }
            >
              E · Abziehen und warten
            </button>)}
            <button
              disabled={!enabled}
              onClick={() =>
                void act(
                  {
                    type: "withdraw",
                    vehicleId: v.id,
                    incidentId: incident.id,
                    mode: "return",
                  },
                  "Fahrzeug abgezogen. Rückfahrt zur Wache wird berechnet.",
                )
              }
            >
              Abziehen und zur Wache
            </button>
            {v.status === 3 && !v.mutualAid && (
              <div className="unit-redirect">
                <label>
                  Anderen Einsatz übernehmen
                  <select
                    aria-label={v.name + ": Zieleinsatz"}
                    value={target?.id ?? ""}
                    onChange={(e) => setTargetId(e.target.value)}
                  >
                    <option value="">Einsatz auswählen …</option>
                    {targets.map((i) => (
                      <option value={i.id} key={i.id}>
                        E {i.number} · {i.address}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  disabled={!enabled || !target}
                  onClick={() =>
                    target &&
                    void act(
                      {
                        type: "redirect",
                        vehicleId: v.id,
                        fromIncidentId: incident.id,
                        incidentId: target.id,
                      },
                      "Fahrzeug wird ab aktueller Position umgeleitet.",
                    )
                  }
                >
                  Auf diesen Einsatz umleiten
                </button>
              </div>
            )}
          </>
        )}
        {!incident && (
          <button
            disabled={!enabled}
            onClick={() =>
              void act(
                { type: "returnToStation", vehicleId: v.id },
                "Rückfahrt zur Heimatwache wird berechnet.",
              )
            }
          >
            H · Zur Wache
          </button>
        )}
      </div>
    </details>
  );
}
